import React, { useState, useEffect } from 'react';
import useSpeechToText from 'react-hook-speech-to-text';
// @ts-ignore
import styles from './AudioToTextInput.module.scss';
import useTranslation from '../../../../../Hooks/useTranslation';

interface Props {
	name: string;
	isRequired?: boolean;
	timeLimitInSeconds?: number;
}

const AudioToTextInput: React.FC<Props> = ({
	name,
	isRequired,
	timeLimitInSeconds,
}) => {
	const { error, isRecording, results, startSpeechToText, stopSpeechToText } =
		useSpeechToText({
			continuous: true,
			useLegacyResults: false,
		});
	const { t } = useTranslation();

	const [textInput, setTextInput] = useState<string>('');
	const capturedSomeText = textInput.length > 0;
	const [remainingTime, setRemainingTime] = useState<number>(0);

	useEffect(() => {
		// @ts-ignore
		setTextInput(results.map(result => result.transcript).join(' '));
	}, [results]);

	useEffect(() => {
		// Recording sometimes stop automatically while there is at least 7 seconds of silence, if there is still some remaining time this will start listening again
		if (remainingTime > 0 && !isRecording) {
			startSpeechToText();
		}
	}, [remainingTime, isRecording]);

	useEffect(() => {
		if (isRecording && timeLimitInSeconds && remainingTime === 0) {
			setRemainingTime(timeLimitInSeconds);
			const interval = setInterval(() => {
				setRemainingTime(prev => {
					if (prev <= 1) {
						clearInterval(interval);
						stopSpeechToText();
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}
	}, [isRecording, timeLimitInSeconds]);

	const handleStartRecording = () => {
		if (!isRecording && !capturedSomeText) {
			startSpeechToText();
		}
	};

	const handleStopRecording = () => {
		if (isRecording && !timeLimitInSeconds) {
			stopSpeechToText();
		}
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.microphoneWrapper}>
				{isRecording && (
					<>
						<span className={styles.ring1} />
						<span className={styles.ring2} />
						<span className={styles.ring3} />
					</>
				)}
				<div
					className={styles.microphone}
					onMouseDown={handleStartRecording}
					onMouseUp={handleStopRecording}
					onMouseLeave={handleStopRecording}
				>
					{capturedSomeText && remainingTime === 0 ? (
						<img
							src='/dist/patientV3/icons/green-check.svg'
							alt='green check'
						/>
					) : (
						<img
							src='/dist/icons/microphone-lines-solid.svg'
							alt='microphone'
						/>
					)}
					{isRecording && t('base.listening')}
					{isRecording && remainingTime !== 0 && <div>{remainingTime} s</div>}
				</div>
			</div>
			<input
				style={{ display: 'none' }}
				type='text'
				value={textInput}
				name={name}
				required={isRequired}
			/>
		</div>
	);
};

export default AudioToTextInput;
